import { useEffect, useRef, useState } from "react";
import { API } from "../api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Bot, X, Send, Loader2 } from "lucide-react";

const SUGGESTIONS = {
  bm: ["Berapa baki cuti saya?", "Bila gaji masuk?", "Bagaimana nak claim mileage?", "Mana payslip bulan lepas?"],
  en: ["What is my leave balance?", "When is payday?", "How do I claim mileage?", "Show my last payslip"],
};

export default function AIAssistant() {
  const { user } = useAuth();
  const { lang } = useLang();
  const bm = lang === "bm";
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0) {
      fetch(`${API}/ai/history`, { credentials: "include" })
        .then((r) => (r.ok ? r.json() : []))
        .then((h) => setMessages(h.map((m) => ({ role: m.role, content: m.content }))))
        .catch(() => {});
    }
  }, [open]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streaming]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const resp = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ message: msg }),
      });
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ") || line === "data: [DONE]") continue;
          try {
            const payload = JSON.parse(line.slice(6));
            if (payload.content) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + payload.content };
                return copy;
              });
            }
          } catch {}
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: bm ? "Maaf, ralat berlaku. Cuba lagi." : "Sorry, an error occurred. Please try again." };
        return copy;
      });
    }
    setStreaming(false);
  };

  if (!user) return null;

  return (
    <>
      <button data-testid="ai-assistant-toggle-button" onClick={() => setOpen(!open)}
        className="fixed bottom-5 right-5 z-50 w-13 h-13 p-3.5 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white shadow-2xl transition-transform hover:scale-105">
        {open ? <X className="w-6 h-6" /> : <Bot className="w-6 h-6" />}
      </button>
      {open && (
        <div data-testid="ai-assistant-panel" className="fixed bottom-20 right-5 z-50 w-[92vw] max-w-sm h-[520px] bg-card border rounded-2xl shadow-2xl flex flex-col overflow-hidden">
          <div className="bg-indigo-600 text-white px-4 py-3 flex items-center gap-2">
            <Bot className="w-5 h-5" />
            <div>
              <p className="font-semibold text-sm">WORKAHOLIC HR AI — HR Assistant</p>
              <p className="text-[11px] text-indigo-200">{bm ? "Bahasa Malaysia / English" : "English / Bahasa Malaysia"}</p>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-3">
            {messages.length === 0 && (
              <div className="space-y-2">
                <p className="text-sm text-slate-600 dark:text-slate-300">
                  {bm ? "Hai! Tanya saya tentang cuti, payslip, tuntutan atau polisi syarikat." : "Hi! Ask me about leave, payslips, claims or company policies."}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {SUGGESTIONS[lang].map((s) => (
                    <button key={s} data-testid={`ai-suggestion-${s.slice(0, 12).replace(/\W/g, "")}`} onClick={() => send(s)}
                      className="text-xs border rounded-full px-3 py-1.5 hover:bg-indigo-50 dark:hover:bg-indigo-950 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800 transition-colors">
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap ${
                  m.role === "user" ? "bg-indigo-600 text-white rounded-br-sm" : "bg-slate-100 dark:bg-slate-800 rounded-bl-sm"}`}>
                  {m.content}
                  {m.role === "assistant" && streaming && i === messages.length - 1 && <span className="inline-block w-1.5 h-3.5 bg-indigo-500 ml-0.5 animate-pulse" />}
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>
          <div className="border-t p-3 flex gap-2">
            <Input data-testid="ai-assistant-chat-input" value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder={bm ? "Tanya soalan HR..." : "Ask an HR question..."} disabled={streaming} />
            <Button data-testid="ai-assistant-send-button" size="icon" className="bg-indigo-600 hover:bg-indigo-700 shrink-0" onClick={() => send()} disabled={streaming || !input.trim()}>
              {streaming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
